import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AREAS, labelOf } from "@/lib/constants";
import { RefreshCcw, Search, ExternalLink } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";
import ModuleHelp from "@/components/help/ModuleHelp";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const ORIGEM_LABEL = {
  manual: "Manual",
  upload_ia: "Upload com IA",
  automatica: "Coleta automática",
  legada: "Importação legada",
};
const ORIGEM_CLASS = {
  manual: "badge-info",
  upload_ia: "badge-gold",
  automatica: "badge-warn",
  legada: "badge-muted",
};

export default function Revisao() {
  const [items, setItems] = useState([]);
  const [openSeed, setOpenSeed] = useState(null);
  const [filters, setFilters] = useState({ q: "", area: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { status: "pendente", limit: 200 };
    if (filters.q) params.q = filters.q;
    if (filters.area) params.area = filters.area;
    const { data } = await api.get("/seeds", { params });
    setItems(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const total = items.length;
  const areasInFila = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.area_tematica_principal] = (c[i.area_tematica_principal] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="space-y-4 -mx-4 md:-mx-6 px-4 md:px-6" data-testid="revisao-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mil text-[var(--gold)]">Validação Humana</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Revisão Humana de Sementes <ModuleHelp module="revisao" /></h1>
          <p className="text-[var(--text-muted)] text-sm mt-2 max-w-2xl">
            {total} sementes pendentes de revisão. Clique em uma linha para abrir o detalhamento e decidir.
          </p>
          <div className="mt-3 max-w-3xl rounded-md border border-amber-200/60 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/40 px-3.5 py-2.5">
            <div className="text-xs uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-300 mb-1">Como revisar uma Semente</div>
            <p className="text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
              Confira se o insumo é prospectivo, se a citação direta está adequada, se a classificação
              metodológica está coerente, se o PESTEL e o recorte geográfico fazem sentido e se o horizonte
              temporal respeita 2030+.
            </p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost text-xs flex items-center gap-2" data-testid="revisao-refresh">
          <RefreshCcw size={12} /> Atualizar
        </button>
      </div>

      {/* Resumo rápido */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {Object.entries(areasInFila).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setFilters((f) => ({ ...f, area: f.area === k ? "" : k }))}
            className={`card-mil p-3 text-left ${filters.area === k ? "border-[var(--gold)]" : ""}`}
            data-testid={`chip-area-${k}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] truncate">{labelOf(AREAS, k)}</div>
            <div className="kpi-num text-xl mt-1">{v}</div>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="card-mil p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="revisao-filters">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className="input-mil pl-9"
            placeholder="Buscar por título, palavra-chave..."
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="revisao-search"
          />
        </div>
        <select className="input-mil" value={filters.area} onChange={(e) => setFilters({ ...filters, area: e.target.value })} data-testid="revisao-filter-area">
          <option value="">Todas áreas</option>
          {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>

      {loading && <div className="font-mono text-xs text-[var(--text-muted)]">CARREGANDO…</div>}

      <div className="card-mil overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="revisao-table">
            <thead>
              <tr>
                {["Título da Semente", "Fonte", "Área", "Classif.", "País", "Inserido por", "Origem", "Data inserção", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 label-mil border-b border-[var(--border)] text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.seed_id}
                  className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                  onClick={() => setOpenSeed(s.seed_id)}
                  data-testid={`row-${s.seed_id}`}
                >
                  <td className="px-4 py-3 max-w-[260px]">
                    <div className="font-semibold truncate">{s.titulo_insumo}</div>
                    <div className="text-[10px] text-[var(--text-muted)] font-mono truncate">{s.seed_id}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[220px] text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[var(--text-muted)] truncate" title={s.source_titulo}>{s.source_titulo || "—"}</span>
                      {s.source_link && (
                        <a
                          href={s.source_link}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[var(--gold)] hover:opacity-80 shrink-0"
                          title="Abrir link da fonte"
                          data-testid={`link-source-${s.seed_id}`}
                        >
                          <ExternalLink size={11} />
                        </a>
                      )}
                      {s.source_has_pdf && (
                        <span className="badge badge-success text-[9px] shrink-0" title="PDF anexado">PDF</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{labelOf(AREAS, s.area_tematica_principal)}</td>
                  <td className="px-4 py-3"><span className="badge badge-gold">{s.classificacao_semente}</span></td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{s.source_pais || "—"}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap max-w-[160px] truncate">
                    {s.created_by_user_name || s.pesquisador_nome || <span className="text-[var(--text-muted)]">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`badge ${ORIGEM_CLASS[s.origem_cadastro] || "badge-muted"}`}>
                      {ORIGEM_LABEL[s.origem_cadastro] || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-[var(--text-muted)] whitespace-nowrap">
                    {fmtDate(s.created_at || s.data_cadastro)}
                  </td>
                  <td className="px-4 py-3"><span className="badge badge-warn">Pendente</span></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenSeed(s.seed_id); }}
                      className="btn-ghost text-xs"
                      data-testid={`open-${s.seed_id}`}
                    >
                      Revisar
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading && (
                <tr><td colSpan={10} className="text-center p-8 text-[var(--text-muted)] text-sm">Nenhuma semente pendente.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} onChanged={load} />
    </div>
  );
}
